import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Euro, 
  TrendingUp, 
  TrendingDown, 
  Calendar as CalendarIcon, 
  DollarSign,
  CreditCard,
  PiggyBank,
  ArrowUpRight,
  ArrowDownRight,
  Edit,
  Download,
  ChevronLeft,
  ChevronRight,
  Save,
  X,
  CheckCircle,
  Trash2,
  Plus
} from "lucide-react";
import { format, addMonths, subMonths } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TreasuryPage = () => {
  const [treasuryData, setTreasuryData] = useState({
    upfront_payments: [],
    monthly_payments: [],
    commissions_due: [],
    commissions_paid: [],
    encaissement_finalise: [],  // Nouvelle section
    forecasts: []
  });
  
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [filterPeriod, setFilterPeriod] = useState("current_month");
  const [customDateRange, setCustomDateRange] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [editingItem, setEditingItem] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  
  // NOUVEAU: Filtres pour l'onglet Avances
  const [avancesFilterName, setAvancesFilterName] = useState("");
  const [avancesFilterSupplier, setAvancesFilterSupplier] = useState("all");
  
  // États pour la gestion des commissions
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedCommercial, setSelectedCommercial] = useState('all'); // Filtre par commercial
  const [commissionFormData, setCommissionFormData] = useState({
    commercial_id: '',
    commercial_name: '',
    client_name: '',
    contract_id: '',
    commission_percentage: 10, // Pourcentage par défaut
    amount: 0,
    payment_date: '',
    notes: '',
    type: 'due', // 'due' ou 'paid'
    auto_generate: false // Si true, génère toutes les échéances automatiquement
  });

  useEffect(() => {
    fetchTreasuryData();
  }, [selectedMonth, filterPeriod, startDate, endDate]);

  const fetchTreasuryData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Préparer les paramètres selon le type de filtre
      let params = {};
      
      if (filterPeriod === 'custom_range') {
        params = {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          period: 'custom_range'
        };
      } else {
        params = {
          month: selectedMonth.toISOString(),
          period: filterPeriod
        };
      }
      
      console.log('[TREASURY FETCH] Params:', params, 'Month:', format(selectedMonth, 'yyyy-MM-dd'));
      
      // Récupérer les données de trésorerie
      const response = await axios.get(`${API}/treasury`, {
        params: params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[TREASURY FETCH] Response:', {
        upfront_count: response.data.upfront_payments?.length || 0,
        upfront_total: response.data.upfront_payments?.reduce((sum, p) => sum + p.amount, 0) || 0,
        commissions_due_count: response.data.commissions_due?.length || 0,
        commissions_paid_count: response.data.commissions_paid?.length || 0
      });
      
      console.log('[TREASURY FETCH] Commissions due:', response.data.commissions_due);
      console.log('[TREASURY FETCH] Commissions paid:', response.data.commissions_paid);
      
      setTreasuryData(response.data);
      
      // Récupérer les commerciaux et télépros
      const usersResponse = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const commerciauxData = usersResponse.data.filter(user => 
        user.role === 'commercial' || user.role === 'telepro' || user.role === 'regie'
      );
      setCommerciaux(commerciauxData);
      
      // Récupérer tous les contrats avec les noms des clients
      const contractsResponse = await axios.get(`${API}/contracts/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(contractsResponse.data);
      
    } catch (error) {
      console.error("Error fetching treasury data:", error);
      toast.error("Erreur lors du chargement des données de trésorerie");
    } finally {
      setLoading(false);
    }
  };

  // DEPRECATED: Prévisions maintenant calculées côté backend avec les vraies dates
  // const calculateForecasts = () => {
  //   Les prévisions sont maintenant dans treasuryData.forecasts
  // };

  const handleEditItem = (item, type) => {
    setEditingItem({ ...item, type });
    setEditFormData(item);
    setShowEditDialog(true);
  };

  const handleDeleteItem = async (itemId, type) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // For commissions, use the commissions endpoint
      if (type === 'commission_due' || type === 'commission_paid') {
        await axios.delete(`${API}/commissions/${itemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } 
      // For treasury payments, use the treasury payments endpoint
      else if (type === 'upfront' || type === 'deferred' || type === 'monthly') {
        await axios.delete(`${API}/treasury/payments/${itemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success("Élément supprimé avec succès");
      
      // Refresh treasury data
      fetchTreasuryData();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(`Erreur lors de la suppression: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/treasury/${editingItem.type}/${editingItem.id}`, editFormData);
      toast.success("Données mises à jour avec succès");
      setShowEditDialog(false);
      setEditingItem(null);
      fetchTreasuryData();
    } catch (error) {
      console.error("Error updating treasury data:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleStatusChange = async (itemId, type, newStatus) => {
    try {
      await axios.patch(`${API}/treasury/${type}/${itemId}`, { status: newStatus });
      toast.success("Statut mis à jour");
      fetchTreasuryData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const exportToXLSX = () => {
    const monthYear = format(selectedMonth, "MMMM_yyyy", { locale: fr });
    
    try {
      // Create a new workbook
      const workbook = XLSX.utils.book_new();
      
      // Sheet 1: Summary
      const summaryData = [
        ['Période', monthYear],
        ['Filtre', filterPeriod === 'current_month' ? 'Mois en cours' : filterPeriod === 'last_3_months' ? '3 derniers mois' : 'Année en cours'],
        [''],
        ['Résumé Financier'],
        ['Revenus Total', totals.totalIncome],
        ['Avances Encaissées', totals.totalUpfront],
        ['Récurrent Mensuel', totals.totalMonthly],
        ['Commissions Dues', totals.totalCommissionsDue],
        ['Marge Brute', totals.margeBrute],
        ['Commissions Payées', totals.totalCommissionsPaid],
        ['Revenus Net', totals.netIncome]
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Résumé');
      
      // Sheet 2: Upfront Payments
      const upfrontHeaders = [['Client', 'Montant (€)', 'Date', 'Statut']];
      const upfrontData = treasuryData.upfront_payments.map(payment => [
        payment.client_name,
        payment.amount,
        payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('fr-FR') :
        payment.paid_date ? new Date(payment.paid_date).toLocaleDateString('fr-FR') :
        payment.due_date ? new Date(payment.due_date).toLocaleDateString('fr-FR') : 'Date non définie',
        payment.status === 'received' ? 'Encaissé' : 'En attente'
      ]);
      const upfrontSheet = XLSX.utils.aoa_to_sheet([...upfrontHeaders, ...upfrontData]);
      XLSX.utils.book_append_sheet(workbook, upfrontSheet, 'Avances');
      
      // Sheet 3: Monthly Payments
      const monthlyHeaders = [['Client', 'Montant/mois (€)', 'Mois restants', 'Durée totale', 'Total restant (€)']];
      const monthlyData = treasuryData.monthly_payments.map(payment => [
        payment.client_name,
        payment.amount,
        payment.months_remaining,
        payment.contract_duration,
        payment.amount * payment.months_remaining
      ]);
      const monthlySheet = XLSX.utils.aoa_to_sheet([...monthlyHeaders, ...monthlyData]);
      XLSX.utils.book_append_sheet(workbook, monthlySheet, 'Paiements Mensuels');
      
      // Sheet 4: Commissions Due
      const commissionsDueHeaders = [['Commercial', 'Client', 'Montant (€)', 'Échéance']];
      const commissionsDueData = treasuryData.commissions_due.map(commission => [
        commission.commercial_name,
        commission.client_name,
        commission.amount,
        new Date(commission.due_date).toLocaleDateString('fr-FR')
      ]);
      const commissionsDueSheet = XLSX.utils.aoa_to_sheet([...commissionsDueHeaders, ...commissionsDueData]);
      XLSX.utils.book_append_sheet(workbook, commissionsDueSheet, 'Commissions À Payer');
      
      // Sheet 5: Commissions Paid
      const commissionsPaidHeaders = [['Commercial', 'Montant (€)', 'Date de paiement', 'Période']];
      const commissionsPaidData = treasuryData.commissions_paid.map(commission => [
        commission.commercial_name,
        commission.amount,
        new Date(commission.paid_date).toLocaleDateString('fr-FR'),
        commission.period
      ]);
      const commissionsPaidSheet = XLSX.utils.aoa_to_sheet([...commissionsPaidHeaders, ...commissionsPaidData]);
      XLSX.utils.book_append_sheet(workbook, commissionsPaidSheet, 'Commissions Payées');
      
      // Generate filename with current date
      const filename = `Tresorerie_${monthYear}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Write and download the file
      XLSX.writeFile(workbook, filename);
      
      toast.success(`Export XLSX téléchargé: ${filename}`);
      
    } catch (error) {
      console.error("Error exporting XLSX:", error);
      toast.error("Erreur lors de la génération du fichier XLSX");
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = direction === 'prev' ? subMonths(selectedMonth, 1) : addMonths(selectedMonth, 1);
    console.log('[TREASURY NAV]', direction, 'From:', format(selectedMonth, 'yyyy-MM'), 'To:', format(newMonth, 'yyyy-MM'));
    setSelectedMonth(newMonth);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const calculateTotals = () => {
    // Séparer les paiements reçus des paiements prévus
    const upfrontReceived = treasuryData.upfront_payments?.filter(p => p.status === 'received').reduce((sum, payment) => sum + payment.amount, 0) || 0;
    const upfrontPending = treasuryData.upfront_payments?.filter(p => p.status === 'pending').reduce((sum, payment) => sum + payment.amount, 0) || 0;
    const totalUpfront = treasuryData.upfront_payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
    
    const monthlyReceived = treasuryData.monthly_payments?.filter(p => p.status === 'received').reduce((sum, payment) => sum + payment.amount, 0) || 0;
    const monthlyPending = treasuryData.monthly_payments?.filter(p => p.status === 'pending').reduce((sum, payment) => sum + payment.amount, 0) || 0;
    const totalMonthly = treasuryData.monthly_payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
    
    const totalCommissionsDue = treasuryData.commissions_due?.reduce((sum, commission) => sum + commission.amount, 0) || 0;
    const totalCommissionsPaid = treasuryData.commissions_paid?.reduce((sum, commission) => sum + commission.amount, 0) || 0;
    
    // Calcul de la marge brute : CA HT TOTAL - Commissions Dues
    const totalIncome = totalUpfront + totalMonthly;
    const margeBrute = totalIncome - totalCommissionsDue;
    
    return {
      // CA HT TOTAL = Total de tous les contrats (reçus + à recevoir)
      totalIncome: totalIncome,
      // Détails par statut
      caReceived: upfrontReceived + monthlyReceived,
      caPending: upfrontPending + monthlyPending,
      totalUpfront,
      totalMonthly,
      upfrontReceived,
      upfrontPending,
      monthlyReceived,
      monthlyPending,
      totalCommissionsDue,
      totalCommissionsPaid,
      margeBrute,  // NOUVEAU: Marge brute
      netIncome: (upfrontReceived + monthlyReceived) - totalCommissionsPaid
    };
  };

  const totals = calculateTotals();

  const mockTreasuryData = {
    // Données mockées supprimées - utiliser uniquement les vraies données du backend
    upfront_payments: [],
    deferred_payments: [],
    monthly_payments: [],
    commissions_due: [],
    commissions_paid: []
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-12 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion de Trésorerie</h1>
          <p className="text-gray-600 mt-1">Encaissements, commissions et prévisionnels</p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Month Navigation */}
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => navigateMonth('prev')}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium min-w-[120px] text-center">
              {format(selectedMonth, "MMMM yyyy", { locale: fr })}
            </span>
            <Button variant="outline" size="sm" onClick={() => navigateMonth('next')}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <Select value={filterPeriod} onValueChange={setFilterPeriod}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current_month">Mois en cours</SelectItem>
              <SelectItem value="last_3_months">3 derniers mois</SelectItem>
              <SelectItem value="current_year">Année en cours</SelectItem>
              <SelectItem value="custom_range">Plage personnalisée</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Interface plage personnalisée */}
          {filterPeriod === 'custom_range' && (
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <Label className="text-sm">Du:</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-32 text-left">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {format(startDate, 'dd/MM/yyyy', { locale: fr })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      locale={fr}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="flex items-center space-x-1">
                <Label className="text-sm">Au:</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-32 text-left">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {format(endDate, 'dd/MM/yyyy', { locale: fr })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      locale={fr}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}
          
          <Button 
            onClick={exportToXLSX} 
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-xlsx-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export XLSX
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">CA HT TOTAL</p>
                <p className="text-3xl font-bold text-emerald-600">{formatCurrency(totals.totalIncome)}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Euro className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-emerald-600 mt-2">
              Reçu: {formatCurrency(totals.caReceived)} | À recevoir: {formatCurrency(totals.caPending)}
            </p>
          </CardContent>
        </Card>

        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avances Totales</p>
                <p className="text-3xl font-bold text-blue-600">{formatCurrency(totals.totalUpfront)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <PiggyBank className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              Reçues: {formatCurrency(totals.upfrontReceived)} | En attente: {formatCurrency(totals.upfrontPending)}
            </p>
          </CardContent>
        </Card>

        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Paiements Mensuels</p>
                <p className="text-3xl font-bold text-amber-600">{formatCurrency(totals.totalMonthly)}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-2">
              Reçus: {formatCurrency(totals.monthlyReceived)} | À recevoir: {formatCurrency(totals.monthlyPending)}
            </p>
          </CardContent>
        </Card>

        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Commissions Dues</p>
                <p className="text-3xl font-bold text-red-600">{formatCurrency(totals.totalCommissionsDue)}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <ArrowDownRight className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-xs text-red-600 mt-2">
              {treasuryData.commissions_due.length} commerciaux à payer
            </p>
          </CardContent>
        </Card>

        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Marge Brute</p>
                <p className="text-3xl font-bold text-purple-600">{formatCurrency(totals.margeBrute)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-purple-600 mt-2">
              CA HT - Commissions dues
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="upfront" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="upfront">Avances</TabsTrigger>
          <TabsTrigger value="deferred">Paiements Différés</TabsTrigger>
          <TabsTrigger value="monthly">Récurrent</TabsTrigger>
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
          <TabsTrigger value="finalized">Encaissement finalisé</TabsTrigger>
          <TabsTrigger value="forecasts">Prévisionnels</TabsTrigger>
        </TabsList>

        {/* Upfront Payments */}
        <TabsContent value="upfront">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <PiggyBank className="w-5 h-5 text-blue-600" />
                <span>Encaissements en Avance</span>
              </CardTitle>
              <CardDescription>
                Paiements d'avance des fournisseurs pour les nouveaux contrats
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* NOUVEAU: Répartition par Fournisseur */}
              {treasuryData.avances_par_fournisseur && treasuryData.avances_par_fournisseur.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-3">📊 Répartition par Fournisseur</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {treasuryData.avances_par_fournisseur.map((item, index) => (
                      <div key={index} className="bg-white p-3 rounded shadow-sm">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">{item.fournisseur}</span>
                          <span className="font-bold text-blue-600">{formatCurrency(item.montant_total)}</span>
                        </div>
                        {item.decommissions !== 0 && item.decommissions != null && (
                          <div className="flex justify-between items-center mt-1 text-xs">
                            <span className="text-red-600">Décommissions:</span>
                            <span className="font-semibold text-red-600">{formatCurrency(item.decommissions)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-200 flex justify-between items-center">
                    <span className="font-semibold text-blue-900">TOTAL</span>
                    <span className="text-xl font-bold text-blue-700">
                      {formatCurrency(treasuryData.avances_par_fournisseur.reduce((sum, item) => sum + item.montant_total + (item.decommissions || 0), 0))}
                    </span>
                  </div>
                </div>
              )}

              {/* NOUVEAU: Filtres pour les avances */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  Filtrer les avances
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Filtre par nom */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du client
                    </label>
                    <input
                      type="text"
                      placeholder="Rechercher par nom..."
                      value={avancesFilterName}
                      onChange={(e) => setAvancesFilterName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {/* Filtre par fournisseur */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fournisseur
                    </label>
                    <select
                      value={avancesFilterSupplier}
                      onChange={(e) => setAvancesFilterSupplier(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Tous les fournisseurs</option>
                      {treasuryData.avances_par_fournisseur && treasuryData.avances_par_fournisseur.map((item, index) => (
                        <option key={index} value={item.fournisseur}>{item.fournisseur}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Bouton reset */}
                {(avancesFilterName || avancesFilterSupplier !== 'all') && (
                  <button
                    onClick={() => {
                      setAvancesFilterName("");
                      setAvancesFilterSupplier("all");
                    }}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Réinitialiser les filtres
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {treasuryData.upfront_payments
                  .filter((payment) => {
                    // Filtre par nom
                    const matchesName = !avancesFilterName || 
                      payment.client_name?.toLowerCase().includes(avancesFilterName.toLowerCase());
                    
                    // Filtre par fournisseur (utilise le champ supplier_name maintenant disponible)
                    const matchesSupplier = avancesFilterSupplier === 'all' || 
                      payment.supplier_name === avancesFilterSupplier;
                    
                    return matchesName && matchesSupplier;
                  })
                  .map((payment) => {
                  const isDecommission = payment.status === 'decommission';
                  return (
                  <div 
                    key={payment.id} 
                    className={`flex items-center justify-between p-4 border-2 rounded-lg ${
                      isDecommission 
                        ? 'bg-violet-50 border-violet-400 hover:bg-violet-100' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {isDecommission && (
                          <Badge className={payment.type_decommission === 'totale' ? 'bg-red-600' : 'bg-orange-600'}>
                            DÉCOMMISSION {payment.type_decommission?.toUpperCase()}
                          </Badge>
                        )}
                        <h4 className="font-medium text-gray-900">{payment.client_name}</h4>
                      </div>
                      <p className="text-sm text-gray-600">
                        {isDecommission ? 'Date: ' : 'Reçu le '}
                        {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('fr-FR') : 
                                payment.paid_date ? new Date(payment.paid_date).toLocaleDateString('fr-FR') : 
                                payment.due_date ? new Date(payment.due_date).toLocaleDateString('fr-FR') : 'Date non définie'}
                      </p>
                      {isDecommission && payment.raison && (
                        <p className="text-xs text-violet-600 mt-1">Raison: {payment.raison}</p>
                      )}
                      {!isDecommission && (
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant={payment.payment_mode === 'full_upfront' ? 'default' : 'secondary'} className="text-xs">
                            {payment.upfront_percentage > 0 ? `${payment.upfront_percentage}% Upfront` : 
                             payment.payment_mode === 'full_upfront' ? '100% Upfront' : 
                             payment.payment_mode === 'split_50_50' ? `${payment.percentage || 0}% (1er paiement)` : 
                             `${payment.percentage || 0}% Upfront`}
                          </Badge>
                          {payment.payment_mode === 'full_upfront' && (
                            <Badge className="bg-emerald-100 text-emerald-800 text-xs">Complet</Badge>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className={`text-lg font-bold ${isDecommission ? 'text-violet-700' : 'text-blue-600'}`}>
                          {formatCurrency(payment.amount)}
                        </div>
                        {!isDecommission && (
                        <Select
                          value={payment.status}
                          onValueChange={(value) => handleStatusChange(payment.id, 'upfront', value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="received">Encaissé</SelectItem>
                            <SelectItem value="pending">En attente</SelectItem>
                          </SelectContent>
                        </Select>
                        )}
                      </div>
                      {!isDecommission && (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditItem(payment, 'upfront')}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteItem(payment.id, 'upfront')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deferred Payments */}
        <TabsContent value="deferred">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-orange-600" />
                <span>Paiements Différés (50/50)</span>
              </CardTitle>
              <CardDescription>
                Seconds paiements dus pour les contrats en mode 50% + 50%
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {treasuryData.deferred_payments?.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-orange-50">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{payment.client_name}</h4>
                      <p className="text-sm text-gray-600">
                        Échéance: {new Date(payment.due_date).toLocaleDateString('fr-FR')}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge className="bg-orange-100 text-orange-800 text-xs">
                          50% (2ème paiement)
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {payment.delay_months} mois après début contrat
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Début contrat: {new Date(payment.contract_start_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="text-lg font-bold text-orange-600">{formatCurrency(payment.amount)}</div>
                        <Select
                          value={payment.status}
                          onValueChange={(value) => handleStatusChange(payment.id, 'deferred', value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">En attente</SelectItem>
                            <SelectItem value="received">Encaissé</SelectItem>
                            <SelectItem value="overdue">En retard</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditItem(payment, 'deferred')}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteItem(payment.id, 'deferred')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Payments */}
        <TabsContent value="monthly">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-amber-600" />
                <span>Paiements Mensuels Récurrents</span>
              </CardTitle>
              <CardDescription>
                Commissions mensuelles sur 24, 36 ou 48 mois selon les contrats
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {treasuryData.monthly_payments.map((payment) => {
                  const isDecommission = payment.status === 'decommission';
                  return (
                  <div 
                    key={payment.id} 
                    className={`flex items-center justify-between p-4 border-2 rounded-lg ${
                      isDecommission 
                        ? 'bg-violet-50 border-violet-400 hover:bg-violet-100' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {isDecommission && (
                          <Badge className="bg-red-600">
                            DÉCOMMISSION TOTALE
                          </Badge>
                        )}
                        <h4 className="font-medium text-gray-900">{payment.client_name}</h4>
                      </div>
                      {isDecommission ? (
                        <>
                          <p className="text-sm text-gray-600">
                            Date: {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('fr-FR') : 
                                   payment.due_date ? new Date(payment.due_date).toLocaleDateString('fr-FR') : 'Date non définie'}
                          </p>
                          {payment.raison && (
                            <p className="text-xs text-violet-600 mt-1">Raison: {payment.raison}</p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-gray-600">
                            {payment.months_remaining} mois restants sur {payment.contract_duration}
                          </p>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-amber-500 h-2 rounded-full" 
                              style={{width: `${((payment.contract_duration - payment.months_remaining) / payment.contract_duration) * 100}%`}}
                            ></div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className={`text-lg font-bold ${isDecommission ? 'text-violet-700' : 'text-amber-600'}`}>
                          {formatCurrency(payment.amount)}
                        </div>
                        {!isDecommission && (
                          <>
                            <div className="text-sm text-gray-500">par mois</div>
                            <div className="text-xs text-gray-400 mt-1">
                              Total restant: {formatCurrency(payment.amount * payment.months_remaining)}
                            </div>
                          </>
                        )}
                      </div>
                      {!isDecommission && (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditItem(payment, 'monthly')}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteItem(payment.id, 'monthly')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )})}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commissions */}
        <TabsContent value="commissions">
          <div className="space-y-6">
            {/* Boutons d'action */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Gestion des Commissions</h2>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  setCommissionFormData({
                    commercial_id: '',
                    commercial_name: '',
                    client_name: '',
                    contract_id: '',
                    amount: 0,
                    payment_date: '',
                    notes: '',
                    type: 'due'
                  });
                  setShowCommissionDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter Commission
              </Button>
            </div>

            {/* NOUVEAU: Tableau Commissions Dues par Personne */}
            {treasuryData.commissions_par_personne && treasuryData.commissions_par_personne.length > 0 && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="font-semibold text-amber-900 mb-3">💰 Commissions Dues par Personne</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {treasuryData.commissions_par_personne.map((item, index) => (
                    <div key={index} className="bg-white p-3 rounded shadow-sm border border-amber-100">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700">{item.commercial_name}</span>
                        <span className="font-bold text-amber-600">{formatCurrency(item.total_commissions)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-amber-300 flex justify-between items-center">
                  <span className="font-semibold text-amber-900">TOTAL COMMISSIONS DUES</span>
                  <span className="text-xl font-bold text-amber-700">
                    {formatCurrency(treasuryData.commissions_par_personne.reduce((sum, item) => sum + item.total_commissions, 0))}
                  </span>
                </div>
              </div>
            )}

            <div className="grid gap-6 md:grid-cols-2">
              {/* Commissions Due */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-red-600 flex items-center justify-between">
                    <span>Commissions à Payer</span>
                    <Badge variant="destructive">
                      {treasuryData.commissions_due?.length || 0}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Filtre par commercial */}
                  <div className="mb-4 space-y-3">
                    <div className="flex items-center gap-4">
                      <Label className="font-semibold">Filtrer par commercial :</Label>
                      <Select value={selectedCommercial} onValueChange={setSelectedCommercial}>
                        <SelectTrigger className="w-64">
                          <SelectValue placeholder="Tous les commerciaux" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les commerciaux</SelectItem>
                          {[...new Set(treasuryData.commissions_due?.map(c => c.commercial_name))].filter(Boolean).map(name => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Total global filtré */}
                    {selectedCommercial !== 'all' && (
                      <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border-2 border-red-300">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-gray-600 font-medium">Total Global pour</p>
                            <p className="text-lg font-bold text-gray-900">{selectedCommercial}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Commissions Dues</p>
                            <p className="text-3xl font-bold text-red-600">
                              {formatCurrency(
                                treasuryData.commissions_due
                                  ?.filter(c => c.commercial_name === selectedCommercial)
                                  .reduce((sum, c) => sum + c.amount, 0) || 0
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {/* DÉCOMMISSIONS (NOUVEAU) */}
                    {treasuryData.decommissions
                      ?.filter(dec => selectedCommercial === 'all' || dec.commercial_name === selectedCommercial)
                      .map((dec) => (
                      <div key={dec.id} className="p-4 bg-violet-50 rounded-lg border-2 border-violet-400">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={dec.type === 'totale' ? 'bg-red-600' : 'bg-orange-600'}>
                                DÉCOMMISSION {dec.type?.toUpperCase()}
                              </Badge>
                            </div>
                            <h5 className="font-semibold text-gray-900">
                              {dec.raison_sociale && `${dec.raison_sociale} - `}{dec.client_name}
                            </h5>
                            <p className="text-sm text-gray-600">
                              {dec.raison && `Raison: ${dec.raison}`}
                            </p>
                            <p className="text-xs text-violet-600">
                              Date: {new Date(dec.date).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-violet-700 text-lg">-{formatCurrency(dec.montant)}</div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* COMMISSIONS NORMALES */}
                    {treasuryData.commissions_due
                      ?.filter(commission => selectedCommercial === 'all' || commission.commercial_name === selectedCommercial)
                      .map((commission) => (
                      <div key={commission.id} className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900">
                              {commission.raison_sociale && `${commission.raison_sociale} - `}{commission.commercial_name}
                            </h5>
                            <p className="text-sm text-gray-600">Client: {commission.client_name}</p>
                            {commission.contract_id && (
                              <p className="text-xs text-blue-600">Contrat: {commission.contract_id}</p>
                            )}
                            <p className="text-xs text-red-600">Échéance: {new Date(commission.due_date).toLocaleDateString('fr-FR')}</p>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-red-600 text-lg">{formatCurrency(commission.amount)}</div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleStatusChange(commission.id, 'commission', 'paid')}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Marquer Payé
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCommissionFormData({
                                id: commission.id,
                                commercial_id: commission.commercial_id || '',
                                commercial_name: commission.commercial_name || '',
                                client_name: commission.client_name || '',
                                contract_id: commission.contract_id || '',
                                amount: commission.amount || 0,
                                payment_date: commission.due_date || '',
                                notes: commission.notes || '',
                                type: 'due'
                              });
                              setShowCommissionDialog(true);
                            }}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Modifier
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteItem(commission.id, 'commission_due')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {(!treasuryData.commissions_due || treasuryData.commissions_due.filter(c => selectedCommercial === 'all' || c.commercial_name === selectedCommercial).length === 0) && (
                      <div className="text-center text-gray-500 py-8">
                        <Euro className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>{selectedCommercial === 'all' ? 'Aucune commission en attente' : `Aucune commission pour ${selectedCommercial}`}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Commissions Paid */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-emerald-600 flex items-center justify-between">
                    <span>Commissions Payées</span>
                    <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                      {treasuryData.commissions_paid?.length || 0}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {treasuryData.commissions_paid?.map((commission) => (
                      <div key={commission.id} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900">
                              {commission.raison_sociale && `${commission.raison_sociale} - `}{commission.commercial_name}
                            </h5>
                            <p className="text-sm text-gray-600">Client: {commission.client_name}</p>
                            {commission.contract_id && (
                              <p className="text-xs text-blue-600">Contrat: {commission.contract_id}</p>
                            )}
                            <p className="text-xs text-emerald-600">Payé le: {new Date(commission.paid_date).toLocaleDateString('fr-FR')}</p>
                            {commission.notes && (
                              <p className="text-xs text-gray-500 italic">Note: {commission.notes}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-emerald-600 text-lg">{formatCurrency(commission.amount)}</div>
                            <Badge className="bg-emerald-100 text-emerald-800 mt-1">Payé</Badge>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCommissionFormData({
                                id: commission.id,
                                commercial_id: commission.commercial_id || '',
                                commercial_name: commission.commercial_name || '',
                                client_name: commission.client_name || '',
                                contract_id: commission.contract_id || '',
                                amount: commission.amount || 0,
                                payment_date: commission.paid_date || '',
                                notes: commission.notes || '',
                                type: 'paid'
                              });
                              setShowCommissionDialog(true);
                            }}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Modifier
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteItem(commission.id, 'commission_paid')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {(!treasuryData.commissions_paid || treasuryData.commissions_paid.length === 0) && (
                      <div className="text-center text-gray-500 py-8">
                        <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Aucune commission payée</p>
                      </div>
                    )}
                  </div>
                </CardContent>
            </Card>
          </div>
        </div>
        </TabsContent>

        {/* Finalized Payments */}
        <TabsContent value="finalized">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Encaissements Finalisés</span>
              </CardTitle>
              <CardDescription>
                Contrats avec encaissements 100% terminés et finalisés
              </CardDescription>
            </CardHeader>
            <CardContent>
              {treasuryData.encaissement_finalise?.length > 0 ? (
                <div className="space-y-3">
                  {treasuryData.encaissement_finalise.map((contract) => (
                    <div key={contract.id} className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="font-medium text-gray-900">{contract.client_name}</h5>
                          <Badge className="bg-blue-100 text-blue-700">{contract.supplier_name}</Badge>
                          <Badge variant="outline" className="text-xs">{contract.contract_type === 'electricity' ? 'Électricité' : 'Gaz'}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">PDL: {contract.pdl}</p>
                        <p className="text-sm text-gray-600">{contract.description}</p>
                        {contract.finalized_date && (
                          <p className="text-xs text-green-600 mt-2">
                            Finalisé le: {new Date(contract.finalized_date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600 text-lg">{formatCurrency(contract.commission)}</div>
                        <p className="text-sm text-gray-500">Commission totale</p>
                        <Badge className="bg-green-100 text-green-800 mt-1">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Finalisé
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun encaissement finalisé</h3>
                  <p className="text-gray-500">
                    Les contrats avec encaissements 100% terminés apparaîtront ici automatiquement.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecasts */}
        <TabsContent value="forecasts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span>Prévisionnels de Trésorerie</span>
              </CardTitle>
              <CardDescription>
                Projections sur les 12 prochains mois
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {treasuryData.forecasts && treasuryData.forecasts.length > 0 ? (
                  treasuryData.forecasts.map((forecast, index) => {
                    const colors = [
                      { bg: 'bg-blue-50', text: 'text-blue-600', textBold: 'text-blue-700' },
                      { bg: 'bg-amber-50', text: 'text-amber-600', textBold: 'text-amber-700' },
                      { bg: 'bg-emerald-50', text: 'text-emerald-600', textBold: 'text-emerald-700' }
                    ];
                    const color = colors[index] || colors[0];
                    
                    // Calculer la marge brute (CA - Commissions)
                    const commissions = forecast.commissions || 0;
                    const margeBrute = forecast.amount - commissions;
                    
                    return (
                      <div key={forecast.period} className={`text-center p-6 ${color.bg} rounded-lg`}>
                        <div className={`text-2xl font-bold ${color.text} mb-2`}>
                          {formatCurrency(forecast.amount)}
                        </div>
                        <div className={`text-sm ${color.textBold} font-medium mb-1`}>
                          {forecast.description}
                        </div>
                        <div className={`text-xs ${color.text} mb-3`}>
                          Encaissements prévisionnels
                        </div>
                        {commissions > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <div className={`text-xs ${color.text} mb-1`}>Commissions: {formatCurrency(commissions)}</div>
                            <div className={`text-sm font-semibold ${color.textBold}`}>
                              Marge: {formatCurrency(margeBrute)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <>
                    <div className="text-center p-6 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600 mb-2">{formatCurrency(0)}</div>
                      <div className="text-sm text-blue-700 font-medium">Prochains 3 mois</div>
                      <div className="text-xs text-blue-600">Encaissements prévisionnels</div>
                    </div>
                    <div className="text-center p-6 bg-amber-50 rounded-lg">
                      <div className="text-2xl font-bold text-amber-600 mb-2">{formatCurrency(0)}</div>
                      <div className="text-sm text-amber-700 font-medium">Prochains 6 mois</div>
                      <div className="text-xs text-amber-600">Encaissements prévisionnels</div>
                    </div>
                    <div className="text-center p-6 bg-emerald-50 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600 mb-2">{formatCurrency(0)}</div>
                      <div className="text-sm text-emerald-700 font-medium">Prochains 12 mois</div>
                      <div className="text-xs text-emerald-600">Encaissements prévisionnels</div>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-8">
                <h4 className="text-lg font-semibold mb-4">Contrats arrivant à échéance (12 mois avant)</h4>
                <div className="space-y-3">
                  {treasuryData.expiring_contracts && treasuryData.expiring_contracts.length > 0 ? (
                    treasuryData.expiring_contracts.map((contract) => (
                      <div key={contract.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border-l-4 border-amber-500">
                        <div>
                          <h5 className="font-medium">{contract.client_name}</h5>
                          <p className="text-sm text-gray-600">
                            {contract.supplier_name} - Fin de contrat: {new Date(contract.end_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-amber-100 text-amber-800">Renouvellement requis</Badge>
                          <p className="text-xs text-amber-600 mt-1">
                            {contract.months_until_expiry} mois avant échéance
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-lg">
                      <p>Aucun contrat à renouveler dans les 12 prochains mois</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-lg font-semibold mb-4">Prévisions annuelles (Vision long terme)</h4>
                <div className="grid gap-4 md:grid-cols-3">
                  {treasuryData.yearly_forecasts && treasuryData.yearly_forecasts.length > 0 ? (
                    treasuryData.yearly_forecasts.map((forecast, index) => {
                      const colors = [
                        { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-500' },
                        { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-500' },
                        { bg: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-500' }
                      ];
                      const color = colors[index] || colors[0];
                      
                      // Calculer la marge brute (CA - Commissions)
                      const commissions = forecast.commissions || 0;
                      const margeBrute = forecast.amount - commissions;
                      
                      return (
                        <div key={forecast.year} className={`text-center p-6 ${color.bg} rounded-lg border-l-4 ${color.border}`}>
                          <div className={`text-3xl font-bold ${color.text} mb-2`}>
                            {forecast.year}
                          </div>
                          <div className={`text-2xl font-semibold ${color.text} mb-2`}>
                            {formatCurrency(forecast.amount)}
                          </div>
                          <div className="text-sm text-gray-600 mb-3">
                            Encaissements prévisionnels
                          </div>
                          {commissions > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-300">
                              <div className="text-xs text-gray-600 mb-1">Commissions: {formatCurrency(commissions)}</div>
                              <div className={`text-lg font-semibold ${color.text}`}>
                                Marge: {formatCurrency(margeBrute)}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <>
                      <div className="text-center p-6 bg-indigo-50 rounded-lg border-l-4 border-indigo-500">
                        <div className="text-3xl font-bold text-indigo-600 mb-2">{new Date().getFullYear() + 1}</div>
                        <div className="text-2xl font-semibold text-indigo-600 mb-2">{formatCurrency(0)}</div>
                        <div className="text-sm text-gray-600">Encaissements prévisionnels</div>
                      </div>
                      <div className="text-center p-6 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                        <div className="text-3xl font-bold text-purple-600 mb-2">{new Date().getFullYear() + 2}</div>
                        <div className="text-2xl font-semibold text-purple-600 mb-2">{formatCurrency(0)}</div>
                        <div className="text-sm text-gray-600">Encaissements prévisionnels</div>
                      </div>
                      <div className="text-center p-6 bg-pink-50 rounded-lg border-l-4 border-pink-500">
                        <div className="text-3xl font-bold text-pink-600 mb-2">{new Date().getFullYear() + 3}</div>
                        <div className="text-2xl font-semibold text-pink-600 mb-2">{formatCurrency(0)}</div>
                        <div className="text-sm text-gray-600">Encaissements prévisionnels</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier les données</DialogTitle>
            <DialogDescription>
              Modifiez les informations de trésorerie
            </DialogDescription>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="amount">Montant (€)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={editFormData.amount || ''}
                  onChange={(e) => setEditFormData({...editFormData, amount: parseFloat(e.target.value)})}
                />
              </div>
              
              {editingItem.type === 'upfront' && (
                <>
                  <div>
                    <Label htmlFor="client_name">Nom du client</Label>
                    <Input
                      id="client_name"
                      value={editFormData.client_name || ''}
                      onChange={(e) => setEditFormData({...editFormData, client_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={editFormData.date ? editFormData.date.split('T')[0] : ''}
                      onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                    />
                  </div>
                </>
              )}
              
              {editingItem.type === 'monthly' && (
                <>
                  <div>
                    <Label htmlFor="client_name">Nom du client</Label>
                    <Input
                      id="client_name"
                      value={editFormData.client_name || ''}
                      onChange={(e) => setEditFormData({...editFormData, client_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="months_remaining">Mois restants</Label>
                    <Input
                      id="months_remaining"
                      type="number"
                      value={editFormData.months_remaining || ''}
                      onChange={(e) => setEditFormData({...editFormData, months_remaining: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contract_duration">Durée totale du contrat</Label>
                    <Input
                      id="contract_duration"
                      type="number"
                      value={editFormData.contract_duration || ''}
                      onChange={(e) => setEditFormData({...editFormData, contract_duration: parseInt(e.target.value)})}
                    />
                  </div>
                </>
              )}
              
              {(editingItem.type === 'commission_due' || editingItem.type === 'commission_paid') && (
                <>
                  <div>
                    <Label htmlFor="commercial_name">Nom du commercial</Label>
                    <Input
                      id="commercial_name"
                      value={editFormData.commercial_name || ''}
                      onChange={(e) => setEditFormData({...editFormData, commercial_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_name">Nom du client</Label>
                    <Input
                      id="client_name"
                      value={editFormData.client_name || ''}
                      onChange={(e) => setEditFormData({...editFormData, client_name: e.target.value})}
                    />
                  </div>
                </>
              )}
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
                <Button onClick={handleSaveEdit}>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog gestion commissions */}
      <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {commissionFormData.id ? 'Modifier Commission' : 'Ajouter Commission'}
            </DialogTitle>
            <DialogDescription>
              Gestion des commissions pour les commerciaux et télépros
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Type de commission */}
            <div>
              <Label>Type de commission</Label>
              <Select 
                value={commissionFormData.type} 
                onValueChange={(value) => setCommissionFormData({...commissionFormData, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="due">À payer</SelectItem>
                  <SelectItem value="paid">Payée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Commercial/Telepro */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Commercial/Télépro</Label>
                <Select 
                  value={commissionFormData.commercial_id} 
                  onValueChange={(value) => {
                    const selectedUser = commerciaux.find(u => u.id === value);
                    setCommissionFormData({
                      ...commissionFormData, 
                      commercial_id: value,
                      commercial_name: selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un utilisateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {commerciaux.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Ou nom manuel</Label>
                <Input
                  value={commissionFormData.commercial_name}
                  onChange={(e) => setCommissionFormData({
                    ...commissionFormData,
                    commercial_name: e.target.value,
                    commercial_id: '' // Reset si saisie manuelle
                  })}
                  placeholder="Saisir le nom manuellement"
                />
              </div>
            </div>

            {/* Client et contrat */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Contrat</Label>
                <Select 
                  value={commissionFormData.contract_id} 
                  onValueChange={(value) => {
                    const selectedContract = contracts.find(c => c.id === value);
                    setCommissionFormData({
                      ...commissionFormData, 
                      contract_id: value,
                      client_name: selectedContract?.client_name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un contrat" />
                  </SelectTrigger>
                  <SelectContent>
                    {contracts.map(contract => (
                      <SelectItem key={contract.id} value={contract.id}>
                        {contract.client_name} - {contract.supplier_name} - Contrat #{contract.id?.substring(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Ou nom client manuel</Label>
                <Input
                  value={commissionFormData.client_name}
                  onChange={(e) => setCommissionFormData({
                    ...commissionFormData,
                    client_name: e.target.value
                  })}
                  placeholder="Nom du client"
                />
              </div>
            </div>

            {/* Pourcentage de commission */}
            {commissionFormData.contract_id && (
              <div>
                <Label>Pourcentage de Commission (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={commissionFormData.commission_percentage}
                  onChange={(e) => setCommissionFormData({
                    ...commissionFormData,
                    commission_percentage: parseFloat(e.target.value) || 0
                  })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Le montant sera calculé automatiquement selon le total du contrat et divisé selon la cadence de paiement
                </p>
              </div>
            )}

            {/* Option de génération automatique */}
            {commissionFormData.contract_id && (
              <div className="flex items-center space-x-2 bg-blue-50 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="auto_generate"
                  checked={commissionFormData.auto_generate}
                  onChange={(e) => setCommissionFormData({
                    ...commissionFormData,
                    auto_generate: e.target.checked
                  })}
                  className="w-4 h-4"
                />
                <Label htmlFor="auto_generate" className="cursor-pointer">
                  Générer automatiquement toutes les échéances selon la cadence du contrat
                </Label>
              </div>
            )}

            {/* Montant et date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!commissionFormData.auto_generate && (
                <div>
                  <Label>Montant (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={commissionFormData.amount}
                    onChange={(e) => setCommissionFormData({
                      ...commissionFormData,
                      amount: parseFloat(e.target.value) || 0
                    })}
                    disabled={commissionFormData.contract_id}
                  />
                  {commissionFormData.contract_id && (
                    <p className="text-xs text-gray-500 mt-1">
                      Calculé automatiquement depuis le pourcentage
                    </p>
                  )}
                </div>
              )}

              {!commissionFormData.auto_generate && (
                <div>
                  <Label>
                    {commissionFormData.type === 'paid' ? 'Date de paiement' : 'Date d\'échéance'}
                  </Label>
                  <Input
                    type="date"
                    value={commissionFormData.payment_date}
                    onChange={(e) => setCommissionFormData({
                      ...commissionFormData,
                      payment_date: e.target.value
                    })}
                  />
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={commissionFormData.notes}
                onChange={(e) => setCommissionFormData({
                  ...commissionFormData,
                  notes: e.target.value
                })}
                placeholder="Notes optionnelles..."
                rows={3}
              />
            </div>

            {/* Boutons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setShowCommissionDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    
                    // Validation des champs obligatoires
                    if (!commissionFormData.commercial_name || (!commissionFormData.amount && !commissionFormData.auto_generate)) {
                      toast.error('Veuillez remplir au moins le nom du commercial et le montant (ou cocher génération automatique)');
                      return;
                    }
                    
                    // Si génération automatique est cochée
                    if (commissionFormData.auto_generate && commissionFormData.contract_id) {
                      console.log('[COMMISSION AUTO-GENERATE] Starting auto-generation...');
                      console.log('[COMMISSION AUTO-GENERATE] Contract ID:', commissionFormData.contract_id);
                      console.log('[COMMISSION AUTO-GENERATE] Commission %:', commissionFormData.commission_percentage);
                      console.log('[COMMISSION AUTO-GENERATE] Commercial ID:', commissionFormData.commercial_id);
                      console.log('[COMMISSION AUTO-GENERATE] API URL:', `${API}/commissions/generate-from-contract/${commissionFormData.contract_id}`);
                      
                      const response = await axios.post(
                        `${API}/commissions/generate-from-contract/${commissionFormData.contract_id}`,
                        {
                          commission_percentage: commissionFormData.commission_percentage,
                          commercial_id: commissionFormData.commercial_id
                        },
                        {
                          headers: { Authorization: `Bearer ${token}` }
                        }
                      );
                      
                      console.log('[COMMISSION AUTO-GENERATE] Success! Response:', response.data);
                      toast.success(`${response.data.count} commission(s) créée(s) ! Vérifiez les mois correspondant aux dates de paiement du contrat.`);
                    } else {
                      // Préparer les données
                      const commissionData = {
                        ...commissionFormData,
                        amount: parseFloat(commissionFormData.amount) || 0
                      };
                      
                      if (commissionFormData.id) {
                        // Modification
                        await axios.put(`${API}/commissions/${commissionFormData.id}`, commissionData, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Commission modifiée avec succès');
                      } else {
                        // Création d'une seule commission
                        await axios.post(`${API}/commissions`, commissionData, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Commission ajoutée avec succès');
                      }
                    }
                    
                    setShowCommissionDialog(false);
                    fetchTreasuryData(); // Recharger les données
                    
                  } catch (error) {
                    console.error('[COMMISSION ERROR] Full error object:', error);
                    console.error('[COMMISSION ERROR] Response data:', error.response?.data);
                    console.error('[COMMISSION ERROR] Response status:', error.response?.status);
                    console.error('[COMMISSION ERROR] Error message:', error.message);
                    toast.error(`Erreur lors de la sauvegarde: ${error.response?.data?.detail || error.message}`);
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {commissionFormData.auto_generate ? 'Générer Toutes les Échéances' : (commissionFormData.id ? 'Modifier' : 'Ajouter')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TreasuryPage;